import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import VolumeUp from "@mui/icons-material/VolumeUp";

import { setSection, updateSection } from "../../../assignmentSlice";
import { getStartingIndex } from "../../../../../utils/resumeUtils";
import { useTTS } from "../../../../../hooks/useTTS";

import UserInputBar from "../../widgets/UserInputBar";
import CommonPage from "../../common/CommonPage";

const normalizeAnswer = (text) => {
  return (text || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/gi, "")
    .replace(/\s+/g, " ")
    .trim();
};

const getAnswer = (page) => page.answer || page.question || "";

const DictationPage = ({ setCurrentStep, isPreview, isReview }) => {
  const dispatch = useDispatch();
  const { section } = useSelector((state) => state.assignment);
  const { speak, stopSpeaking } = useTTS();

  const [pageList, setPageList] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [userInput, setUserInput] = useState("");
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  const currentPage = pageList[currentIndex] || {
    question: "",
    answer: "",
    input: "",
    isSubmitted: false,
    isCorrect: false,
  };

  const playCurrentSentence = () => {
    const sentence = currentPage.question || currentPage.answer;
    if (!sentence) return;
    stopSpeaking();
    speak({ text: sentence });
  };

  useEffect(() => {
    if (pageList.length > 0) return;

    if (!isPreview && section.answer.length > 0) {
      setPageList(section.answer);
      setCurrentIndex(getStartingIndex(section.resumeIndex));
      return;
    }

    setPageList(
      section.question.data.map((question) => {
        return {
          ...question,
          answer: question.answer || question.question,
          input: "",
          isSubmitted: false,
          isCorrect: false,
        };
      })
    );
    setCurrentIndex(0);
  }, [section, isPreview, pageList.length]);

  useEffect(() => {
    if (currentIndex < 0) return;

    if (currentPage.isSubmitted) {
      setUserInput(currentPage.isCorrect ? currentPage.input : getAnswer(currentPage));
    } else {
      setUserInput(currentPage.input || "");
    }
  }, [
    currentIndex,
    currentPage.input,
    currentPage.isSubmitted,
    currentPage.isCorrect,
    currentPage.answer,
    currentPage.question,
  ]);

  useEffect(() => {
    if (currentIndex < 0 || !currentPage.question) return;
    stopSpeaking();
    speak({ text: currentPage.question });
  }, [currentIndex, currentPage.question, speak, stopSpeaking]);

  useEffect(() => {
    return () => stopSpeaking();
  }, [stopSpeaking]);

  const handleChange = (e) => {
    setUserInput(e.target.value);
  };

  const handleNextPage = () => {
    if (currentIndex < pageList.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      let isAllAnswered = true;

      pageList.forEach((page) => {
        if (!page.isSubmitted) {
          isAllAnswered = false;
        }
      });

      if (isAllAnswered) {
        stopSpeaking();
        if (!isPreview && !isReview) {
          dispatch(
            updateSection({
              sectionId: section.id,
              data: { is_finished: true, answer: pageList },
            })
          );
        }
        alert("학습이 완료되었습니다.");
        setCurrentStep(2);
      } else {
        alert("문제를 덜 풀었습니다.");
      }
    }
  };

  const handlePreviousPage = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleSubmit = () => {
    if (currentPage.isSubmitted) return;
    if (userInput.trim() === "") {
      return alert("답을 입력해주세요.");
    }

    const answer = getAnswer(currentPage);
    const isAnswerCorrect = normalizeAnswer(userInput) === normalizeAnswer(answer);

    setOverlayVisible(true);
    setIsCorrect(isAnswerCorrect);

    const newPageList = pageList.map((page, pageIndex) => {
      if (pageIndex === currentIndex) {
        return {
          ...page,
          input: userInput,
          isCorrect: isAnswerCorrect,
          isSubmitted: true,
        };
      }
      return page;
    });
    setPageList(newPageList);
    dispatch(setSection({ ...section, answer: newPageList }));

    setTimeout(() => {
      setOverlayVisible(false);
      if (!isAnswerCorrect) {
        setUserInput(answer);
      }
    }, 600);
  };

  return (
    <CommonPage
      currentIndex={currentIndex}
      handleNextPage={handleNextPage}
      handlePreviousPage={handlePreviousPage}
      totalNo={pageList.length}
      currentPage={currentPage}
      overlayVisible={overlayVisible}
      isCorrect={isCorrect}
      handleSubmit={handleSubmit}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "50px",
          width: "90%",
        }}
      >
        <div style={{ fontSize: Math.min(window.innerWidth, window.screen.width) <= 768 ? "14px" : "20px", fontWeight: 700, textAlign: "center" }}>문장을 듣고 영어로 받아쓰세요.</div>
        <button
          type="button"
          aria-label="문장 다시 듣기"
          onClick={playCurrentSentence}
          style={{
            width: "78px",
            height: "78px",
            borderRadius: "50%",
            border: "1px solid #D9D9D9",
            backgroundColor: "#F6F8FA",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            boxShadow: "rgba(0, 0, 0, 0.18) 0px 5px 12px",
            cursor: "pointer",
          }}
        >
          <VolumeUp sx={{ fontSize: "42px", color: "#2D2D2D" }} />
        </button>
        <div style={{ width: "80%", position: "relative" }}>
          <UserInputBar
            userInput={userInput}
            handleChange={handleChange}
            type={"sentence"}
            handleSetAnswer={handleSubmit}
            onlyUnderline={true}
            isSubmitted={currentPage.isSubmitted}
          />
        </div>
      </div>
    </CommonPage>
  );
};

export default DictationPage;
